<script setup>
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps({
  entry: {
    type: Object,
    default: null,
  },
  isOpen: {
    type: Boolean,
    default: false,
  },
  bots: {
    type: Array,
    default: () => [],
  },
  selectedBotId: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(['save', 'close']);

const form = ref({
  agent_bot_id: null,
  question: '',
  answer: '',
});

// Get selected bot name for display
const selectedBotName = computed(() => {
  const botId = form.value.agent_bot_id;
  if (!botId) return t('OMNI.KNOWLEDGE_BASE.NO_BOT_SELECTED');
  const bot = props.bots.find(b => b.id === botId);
  return bot?.name || '';
});

watch(
  () => props.isOpen,
  isOpen => {
    if (isOpen) {
      if (props.entry) {
        form.value = {
          agent_bot_id: props.entry.agent_bot_id || props.selectedBotId || null,
          question: props.entry.question || '',
          answer: props.entry.answer || '',
        };
      } else {
        form.value = {
          agent_bot_id: props.selectedBotId || null,
          question: '',
          answer: '',
        };
      }
    }
  }
);

const handleSubmit = () => {
  emit('save', { ...form.value });
};

const handleClose = () => {
  emit('close');
};
</script>

<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
  >
    <div
      class="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
    >
      <!-- Header -->
      <div
        class="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between"
      >
        <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
          {{
            entry?.id
              ? t('OMNI.KNOWLEDGE_BASE.EDIT_ENTRY')
              : t('OMNI.KNOWLEDGE_BASE.NEW_ENTRY')
          }}
        </h2>
        <button
          class="text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
          @click="handleClose"
        >
          <span class="i-lucide-x w-5 h-5" />
        </button>
      </div>

      <!-- Form -->
      <form
        class="flex-1 overflow-y-auto p-6 space-y-4"
        @submit.prevent="handleSubmit"
      >
        <!-- Bot Display (read-only) -->
        <div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            {{ t('OMNI.KNOWLEDGE_BASE.BOT') }}
          </label>
          <div
            class="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-md bg-slate-50 dark:bg-slate-700 text-slate-700 dark:text-slate-300 flex items-center gap-2"
          >
            <span class="i-lucide-bot w-4 h-4" />
            <span>{{ selectedBotName }}</span>
          </div>
        </div>

        <!-- Question -->
        <div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            {{ t('OMNI.KNOWLEDGE_BASE.QUESTION') }} *
          </label>
          <textarea
            v-model="form.question"
            rows="3"
            required
            class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            :placeholder="t('OMNI.KNOWLEDGE_BASE.QUESTION_PLACEHOLDER')"
          />
        </div>

        <!-- Answer -->
        <div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            {{ t('OMNI.KNOWLEDGE_BASE.ANSWER') }} *
          </label>
          <textarea
            v-model="form.answer"
            rows="5"
            required
            class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            :placeholder="t('OMNI.KNOWLEDGE_BASE.ANSWER_PLACEHOLDER')"
          />
        </div>
      </form>

      <!-- Footer -->
      <div
        class="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2"
      >
        <button
          type="button"
          class="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md"
          @click="handleClose"
        >
          {{ t('OMNI.COMMON.CANCEL') }}
        </button>
        <button
          type="submit"
          class="px-4 py-2 text-sm font-medium text-white bg-woot-500 hover:bg-woot-600 rounded-md"
          @click="handleSubmit"
        >
          {{ t('OMNI.COMMON.SAVE') }}
        </button>
      </div>
    </div>
  </div>
</template>
